import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { Card } from '@apigrowfinance/pbolt-card';
import CurrencyFormatter, {
    formatCurrency,
} from '@apigrowfinance/pbolt-currency-formatter';
import { getFormattedDate } from '@apigrowfinance/pbolt-date-formatter';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Bar,
    BarChart,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
} from 'recharts';

import { Bucket } from '@clients/domains/delinquency/delinquencyBuckets';
import LoadingErrorMessage from '@components/LoadingErrorMessage/LoadingErrorMessage';
import Cursor from '@pages/delinquency/graph/Cursor';
import Legend from '@pages/delinquency/graph/Legend';
import CustomTooltip from '@pages/delinquency/graph/Tooltip';

interface DelinquencyGraphProps {
    delinquency: Bucket[];
    isLoading?: boolean;
    isError?: boolean;
    currency: string;
}
const Graph = ({
    delinquency,
    isLoading = false,
    isError = false,
    currency,
}: DelinquencyGraphProps) => {
    const { formatNumber } = useIntl();
    const { theme } = useTheme();

    const barWidth = 8;
    const barCursorWidth = 30;

    if (isError) {
        return (
            <Card>
                <LoadingErrorMessage />
            </Card>
        );
    }
    const chartDelinquency = delinquency
        ?.map((bucket) => {
            return {
                cycle: bucket?.statement_cycle,
                createdAt: getFormattedDate({
                    date: bucket?.created_at,
                }),
                paid: bucket?.bucket_amount - bucket?.bucket_balance,
                overdue: bucket?.bucket_balance,
            };
        })
        .reverse();
    return (
        <>
            <Typography component="div" variant="overline" mb={1.65}>
                {isLoading ? (
                    <Skeleton
                        data-testid="bar-chart-title-skeleton"
                        width={120}
                    />
                ) : (
                    <FormattedMessage
                        id="DelinquencyGraph.graphAreaTitle"
                        defaultMessage="Delinquency over the last 12 cycles"
                    />
                )}
            </Typography>
            {isLoading ? (
                <Skeleton
                    data-testid="bar-chart-loading-skeleton"
                    variant="rectangular"
                    width="100%"
                    height={391}
                />
            ) : (
                <Paper elevation={0} sx={{ position: 'relative' }}>
                    <Box
                        pt={3}
                        px={2.5}
                        mb={0.5}
                        sx={{ display: 'flex', position: 'relative' }}
                        justifyContent="space-between">
                        <Box>
                            <Typography variant="overline">
                                <FormattedMessage
                                    id="DelinquencyGraph.totalOverdueAmount"
                                    defaultMessage="Total overdue amount"
                                />
                            </Typography>
                            <Typography variant="h2" component="div">
                                <CurrencyFormatter
                                    currency={currency}
                                    amount={
                                        delinquency?.[0]?.bucket_amount || 0
                                    }
                                />
                            </Typography>
                        </Box>
                        <Box>
                            <Legend isLoading={isLoading} />
                        </Box>
                    </Box>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            margin={{
                                top: 36,
                                right: 50,
                                left: 50,
                                bottom: 35,
                            }}
                            width={500}
                            height={300}
                            data={chartDelinquency}>
                            <XAxis
                                dy={14}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(number) =>
                                    chartDelinquency?.find(
                                        (bucket) => bucket?.cycle == number,
                                    )?.createdAt
                                }
                                dataKey="cycle"
                                scale="point"
                                padding={{ left: 10, right: 10 }}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(number: number) =>
                                    formatCurrency({
                                        amount: number,
                                        currency: currency,
                                        formatNumber: formatNumber,
                                    })
                                }
                            />
                            <Tooltip
                                cursor={
                                    <Cursor
                                        width={barCursorWidth}
                                        barWidth={barWidth}
                                    />
                                }
                                wrapperStyle={{ outline: 'none' }}
                                content={<CustomTooltip />}
                            />
                            <Bar
                                animationBegin={0}
                                animationDuration={400}
                                animationEasing="linear"
                                barSize={barWidth}
                                dataKey="overdue"
                                stackId="a"
                                fill={theme.palette.error.dark}
                                background={{
                                    fill:
                                        theme.palette.mode == 'dark'
                                            ? 'background.default'
                                            : theme.palette.grey[200],
                                }}
                                name="Overdue"
                            />
                            <Bar
                                animationBegin={400}
                                animationDuration={400}
                                animationEasing="linear"
                                barSize={barWidth}
                                dataKey="paid"
                                stackId="a"
                                fill={theme.palette.success.dark}
                                name="Paid"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            )}
        </>
    );
};
export default Graph;
